// @flow
import * as React from 'react'
import { type ContextRouter, Route, Switch } from 'react-router-dom'
import uri from 'urijs'
import { App, NotFoundRoute, WonderschoolRouter } from 'framework'
import Loader from 'ui/Loader'
import Loadable from 'react-loadable'
import LoadingPage from 'ui/LoadingPage'
import NoMatch from 'ui/NoMatch'
import moment from 'moment'
import '../../i18n'

import BackToSchoolBanner from './shared/BackToSchoolBanner'

const BannerWrapper = (props: { children: React.Node }) => (
  <React.Fragment>
    {moment().isBefore('2019-09-16') && <BackToSchoolBanner />}
    {props.children}
  </React.Fragment>
)

const createLoader = (loader: any) => Loadable({ loader, loading: Loader })
const createMarcomLoader = (loader: any) => Loadable({ loader, loading: LoadingPage })

const CareCheckInPage = createMarcomLoader(() => import('./CareCheckInPage'))
const DirectorCheckInPage = createMarcomLoader(() => import('./DirectorCheckInPage'))
const ForgotPasswordPage = createMarcomLoader(() => import('./ForgotPasswordPage'))
const PartnerPage = createMarcomLoader(() => import('./PartnerPage'))
const GatheringPage = createMarcomLoader(() => import('./GatheringPage'))
const InvoiceIndexPage = createMarcomLoader(() => import('./InvoiceIndexPage'))
const InvoiceShowPage = createMarcomLoader(() => import('./InvoiceShowPage'))
const PasswordResetPage = createMarcomLoader(() => import('./PasswordResetPage'))
const ConvertProspectPage = createMarcomLoader(() => import('./ConvertProspectPage'))
const ConfirmUnenrollmentPage = createMarcomLoader(() => import('./ConfirmUnenrollmentPage'))
const ResourcesPage = createMarcomLoader(() => import('./ResourcesPage'))
const ZoopaloozaLandingPage = createMarcomLoader(() => import('./ZoopaloozaLandingPage'))
const StartRouter = createLoader(() => import('./StartRouter'))
const QuestionnaireReviewPage = createLoader(() => import('./QuestionnaireReviewPage'))
const HandbookPage = createLoader(() => import('./HandbookPage'))

const PATH_PARTNER_SURVEY = '/partner-survey'

export default class HomeRouter extends React.Component<{}> {
  render = () => {
    // HACK (jake): ignore error as we'd need to upgrade flow to get it to recognize React.Suspense
    // $FlowFixMe
    const Suspense = React.Suspense

    return (
      <Suspense fallback={<Loader />}>
        <App>
          <WonderschoolRouter>{this.renderRoutes()}</WonderschoolRouter>
        </App>
      </Suspense>
    )
  }

  renderRoutes = () => {
    return (
      <Switch>
        <Route exact path="/invoices" component={InvoiceIndexPage} />
        <Route exact path="/invoices/:id" render={this.renderInvoiceShowPage} />
        <Route path={PATH_PARTNER_SURVEY} component={PartnerPage} />
        <Route path="/care-check-ins/:careCheckInId" render={this.renderCareCheckInPage} />
        <Route
          path="/director-check-ins/:directorCheckInId"
          render={this.renderDirectorCheckInPage}
        />
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/password-reset/:token" render={this.renderPasswordResetPage} />
        <Route exact path="/sign-up/:token" render={this.renderConvertProspectPage} />

        <Route exact path="/confirm-unenrollment/:id" render={this.renderConfirmUnenrollmentPage} />
        <Route path="/start" component={StartRouter} />
        <Route exact path="/resource-center" component={ResourcesPage} />
        <Route exact path="/zoopalooza" component={ZoopaloozaLandingPage} />
        <Route exact path="/handbooks/:schoolSlug" component={this.renderHandbookPage} />

        <Route
          exact
          path="/beta/questionnaire-review/:instanceId"
          component={this.renderQuestionnaireReviewPage}
        />

        <Route exact path="/:school/apply" component={NoMatch} />
        <Route path="/parent" component={NoMatch} />

        <Route exact path="/:state/:city/events/:slug" render={this.renderGatheringPage} />
        <NotFoundRoute />
      </Switch>
    )
  }

  renderInvoiceShowPage = (routerProps: ContextRouter) => {
    const id: string = (routerProps.match.params.id: any)

    return <InvoiceShowPage invoiceId={id} />
  }

  renderConvertProspectPage = (routerProps: ContextRouter) => {
    const token: string = routerProps.match.params.token || ''
    const destination = uri(routerProps.location.search).search(true).destination

    return <ConvertProspectPage token={token} destination={destination} />
  }

  renderCareCheckInPage = (routerProps: ContextRouter) => {
    const careCheckInId: string = (routerProps.match.params.careCheckInId: any)
    const overallRatingRaw = uri(routerProps.location.search).search(true).rating
    const overallRatingParsed = parseInt(overallRatingRaw, 10)
    const overallRating = Number.isNaN(overallRatingParsed) ? null : overallRatingParsed

    return <CareCheckInPage careCheckInId={careCheckInId} overallRating={overallRating} />
  }

  renderDirectorCheckInPage = (routerProps: ContextRouter) => {
    const directorCheckInId: string = (routerProps.match.params.directorCheckInId: any)

    return <DirectorCheckInPage directorCheckInId={directorCheckInId} />
  }

  renderPasswordResetPage = (routerProps: ContextRouter) => {
    const token: string = (routerProps.match.params.token: any)
    const destination = uri(routerProps.location.search).search(true).destination

    return <PasswordResetPage token={token} destination={destination} />
  }

  renderGatheringPage = (routerProps: ContextRouter) => {
    const slug = routerProps.match.params.slug
    const id = (slug && parseInt(slug.split(/-+/).pop(), 10)) || -1

    return (
      <BannerWrapper>
        <GatheringPage gatheringId={id} />
      </BannerWrapper>
    )
  }

  renderHandbookPage = (routerProps: ContextRouter) => {
    const schoolSlug: string = routerProps.match.params.schoolSlug || ''

    return (
      <BannerWrapper>
        <HandbookPage schoolSlug={schoolSlug} />
      </BannerWrapper>
    )
  }

  renderConfirmUnenrollmentPage = (routerProps: ContextRouter) => {
    const id: number = Number(routerProps.match.params.id) || -1
    return <ConfirmUnenrollmentPage enrollmentId={id} />
  }

  renderQuestionnaireReviewPage = (routerProps: ContextRouter) => {
    const instanceId: string = routerProps.match.params.instanceId || ''

    return <QuestionnaireReviewPage instanceId={instanceId} />
  }
}
